import { useCallback, useEffect, useState } from "react";
import { onFcmForegroundMessage } from "@safers/pwa";
import { getNotices, putNotice, type StoredNotice } from "@/lib/noticeStore";

export type WorkerNotice = StoredNotice;

interface NoticePayload {
  title?: string;
  body?: string;
  data?: { siteUuid?: string; title?: string; body?: string; message?: string };
}

function toNotice(p: NoticePayload, uuid: string): WorkerNotice {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: p.title || p.data?.title || "현장 관리자 안내",
    message: p.body || p.data?.body || p.data?.message || "",
    receivedAt: new Date().toISOString(),
    siteUuid: p.data?.siteUuid || uuid,
  };
}

export function useWorkerNotices(uuid: string) {
  const [notices, setNotices] = useState<WorkerNotice[]>([]);
  const [banner, setBanner] = useState<WorkerNotice | null>(null);

  const refresh = useCallback(async () => {
    if (!uuid) {
      setNotices([]);
      return;
    }
    setNotices(await getNotices(uuid));
  }, [uuid]);

  /*
    현장이 바뀌면 앞 현장의 배너를 즉시 지운다. effect 가 아니라 **렌더 중에 조정**한다 —
    effect 는 그린 뒤에 돌아 앞 현장 안내가 한 프레임 스쳐 보인다.
    (이전값을 ref 가 아니라 state 로 잡는 건 렌더 중 ref 접근이 금지돼서다)
  */
  const [prevUuid, setPrevUuid] = useState(uuid);
  if (prevUuid !== uuid) {
    setPrevUuid(uuid);
    setBanner(null);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = uuid ? await getNotices(uuid) : [];
      if (!cancelled) setNotices(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [uuid]);

  const accept = useCallback(
    async (p: NoticePayload) => {
      if (!uuid) return;
      const siteUuid = p.data?.siteUuid;
      if (siteUuid && siteUuid !== uuid) return;
      const notice = toNotice(p, uuid);
      await putNotice(notice);
      setBanner(notice);
      await refresh();
    },
    [uuid, refresh]
  );

  useEffect(() => {
    if (!uuid || typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    function onMessage(e: MessageEvent) {
      const data = e.data as { type?: string; payload?: NoticePayload } | null;
      if (!data || data.type !== "safers-notice") return;
      const p = data.payload ?? {};
      if (p.data?.siteUuid && p.data.siteUuid !== uuid) return;
      setBanner(toNotice(p, uuid));
      refresh();
    }
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [uuid, refresh]);

  useEffect(() => {
    if (!uuid) return;
    let unsub: (() => void) | undefined;
    try {
      unsub = onFcmForegroundMessage((payload) => {
        accept({
          title: payload.notification?.title,
          body: payload.notification?.body,
          data: payload.data as NoticePayload["data"],
        });
      });
    } catch {
      unsub = undefined;
    }
    return () => unsub?.();
  }, [uuid, accept]);

  const dismissBanner = useCallback(() => setBanner(null), []);

  return { notices, banner, dismissBanner };
}
